import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import compose from 'recompose/compose'
import PropTypes from 'prop-types'
import { withWidth, Drawer, makeStyles } from '@material-ui/core'
import { useDispatch } from 'react-redux'

import { Creators as NotificationsActions } from '~/store/ducks/notifications'
import FlashMessage from '~/components/FlashMessage'
import Sidebar from './components/Sidebar'
import Topbar from './components/Topbar'

import styles from './styles'

const useStyles = makeStyles(styles)

function DashboardContainer(props) {
  const { width, title, children, noSidebar, hasMonthCountdown } = props
  const {
    props: {
      location: { search },
    },
  } = children
  const titleFromQueryParams = decodeURI(search.replace('?name=', ''))
  const classes = useStyles()
  const isMobile = ['xs', 'sm', 'md'].includes(width)
  const [isOpen, setIsOpen] = useState(!isMobile)
  const dispatch = useDispatch()

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleToggleOpen = () => {
    setIsOpen((prevState) => !prevState)

    // necessary for redrawing charts when opening and closing sidebar menu
    // eslint-disable-next-line no-undef
    setTimeout(() => window.dispatchEvent(new Event('resize')), 200)
  }

  const shiftTopbar = isOpen && !isMobile && !noSidebar
  const shiftContent = isOpen && !isMobile && !noSidebar

  const caidTitle = titleFromQueryParams || title

  document.title = caidTitle === '' ? 'Caid' : `${caidTitle} - Caid`

  useEffect(() => {
    const POLLING_INTERVAL = 1000 * 60 * 1 // 1 minute
    const IDLE_TIMEOUT = 1000 * 60 * 5 // 5 minutes of inactivity

    let intervalId = null
    let idleTimeoutId = null
    let isIdle = false
    let lastActivityTimestamp = Date.now()

    const fetchNotifications = () => {
      dispatch(NotificationsActions.getNotificationsRequest())
    }

    const startPolling = () => {
      if (!intervalId) {
        intervalId = setInterval(fetchNotifications, POLLING_INTERVAL)
      }
    }

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId)
        intervalId = null
      }
    }

    const handleInactivity = () => {
      isIdle = true
      stopPolling()
    }

    const resetIdleTimer = () => {
      const now = Date.now()

      if (isIdle) {
        isIdle = false
        lastActivityTimestamp = now
        if (!document.hidden) {
          fetchNotifications()
          startPolling()
        }
      } else if (now - lastActivityTimestamp < 5000 && idleTimeoutId) {
        return
      }

      lastActivityTimestamp = now

      if (idleTimeoutId) {
        clearTimeout(idleTimeoutId)
      }
      idleTimeoutId = setTimeout(handleInactivity, IDLE_TIMEOUT)
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling()
        if (idleTimeoutId) {
          clearTimeout(idleTimeoutId)
          idleTimeoutId = null
        }
      } else {
        isIdle = false
        fetchNotifications()
        startPolling()
        resetIdleTimer()
      }
    }

    fetchNotifications()
    startPolling()
    resetIdleTimer()

    const activityEvents = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
    ]

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetIdleTimer, { passive: true })
    })

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      stopPolling()
      if (idleTimeoutId) {
        clearTimeout(idleTimeoutId)
      }
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetIdleTimer)
      })
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [dispatch])

  return (
    <>
      <Topbar
        className={classNames(classes.topbar, {
          [classes.topbarShift]: shiftTopbar,
        })}
        isSidebarOpen={isOpen}
        onToggleSidebar={handleToggleOpen}
        title={caidTitle}
        noSidebar={noSidebar}
        hasMonthCountdown={hasMonthCountdown}
      />
      {!noSidebar && (
        <Drawer
          anchor="left"
          classes={{ paper: classes.drawerPaper }}
          onClose={handleClose}
          open={isOpen}
          variant={isMobile ? 'temporary' : 'persistent'}
        >
          <Sidebar
            className={classes.sidebar}
            isMobile={isMobile}
            handleDrawerClose={handleClose}
          />
        </Drawer>
      )}
      <main
        className={classNames(classes.content, {
          [classes.contentShift]: shiftContent,
        })}
      >
        <FlashMessage />
        {children}
      </main>
    </>
  )
}

DashboardContainer.propTypes = {
  children: PropTypes.element,
  title: PropTypes.string,
  noSidebar: PropTypes.bool,
  width: PropTypes.string,
  hasMonthCountdown: PropTypes.bool,
}

DashboardContainer.defaultProps = {
  children: <></>,
  title: '',
  width: '',
  noSidebar: false,
  hasMonthCountdown: false,
}

export default compose(withWidth())(DashboardContainer)
